import React, { useEffect, createContext, useContext, useReducer } from 'react';
import { generateSeedData } from '../utils/seedData';
import { generateReference } from '../utils/formatters';
// --- Types ---
export type KycTier = 0 | 1 | 2 | 3;
export type KycDocument = {
  type: 'bvn' | 'nin' | 'voters_card' | 'drivers_license' | 'passport' | 'utility_bill' | 'bank_statement' | 'selfie';
  status: 'pending' | 'verified' | 'rejected';
  submittedAt: string;
  verifiedAt?: string;
  value?: string; // masked value like "****1234"
};
export type KycData = {
  tier: KycTier;
  status: 'unverified' | 'pending' | 'verified';
  documents: KycDocument[];
  dailyLimit: number;
  tier1CompletedAt?: string;
  tier2CompletedAt?: string;
  tier3CompletedAt?: string;
};
export type User = {
  id: string;
  name: string;
  email: string;
  phone: string;
  walletBalance: number;
  avatar: string;
  kyc: KycData;
  accountType?: 'individual' | 'organization';
  dateOfBirth?: string;
  organizationName?: string;
  cacNumber?: string;
  officeAddress?: string;
};
export type Campaign = {
  id: string;
  title: string;
  description: string;
  targetAmount: number;
  raisedAmount: number;
  category: string;
  endDate: string;
  status: string;
  imageUrl: string;
  createdAt: string;
  userId: string;
  organizerName: string;
  // New optional fields for GoFundMe-like features
  donorCount?: number;
  isUrgent?: boolean;
  shortDescription?: string;
  location?: {
    state: string;
    city: string;
  };
  verificationStatus?: 'pending' | 'verified' | 'rejected';
  organizerAvatar?: string;
  story?: string;
  useOfFunds?: Array<{
    item: string;
    percentage: number;
  }>;
};
export type Transaction = {
  id: string;
  type: string;
  title: string;
  amount: number;
  status: string;
  date: string;
  reference: string;
};
export type Transfer = {
  id: string;
  sourceCurrency: string;
  sourceAmount: number;
  ngnAmount: number;
  recipientName: string;
  bankName: string;
  accountNumber: string;
  fee: number;
  exchangeRate: number;
  status: string;
  date: string;
  reference: string;
};
export type Property = {
  id: string;
  address: string;
  type: string;
  monthlyRent: number;
  role: 'Tenant' | 'Landlord';
  landlordName?: string;
  tenantName?: string;
  nextDueDate: string;
};
export type MaintenanceRequest = {
  id: string;
  propertyId: string;
  issueType: string;
  description: string;
  urgency: string;
  status: string;
  createdAt: string;
};
export type Donation = {
  id: string;
  campaignId: string;
  donorId: string;
  donorName: string;
  amount: number;
  tipAmount: number;
  message: string;
  isAnonymous: boolean;
  isRecurring: boolean;
  paymentMethod: 'card' | 'bank_transfer' | 'wallet';
  transactionReference: string;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
};
export type Payout = {
  id: string;
  campaignId: string;
  campaignTitle: string;
  organizerId: string;
  amount: number;
  fee: number;
  netAmount: number;
  bankName: string;
  accountNumber: string;
  accountName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  completedAt?: string;
};
type AppState = {
  isAuthenticated: boolean;
  user: User | null;
  campaigns: Campaign[];
  transactions: Transaction[];
  transfers: Transfer[];
  properties: Property[];
  maintenanceRequests: MaintenanceRequest[];
  donations: Donation[];
  payouts: Payout[];
};
type Action = {
  type: 'LOGIN';
  payload: User;
} | {
  type: 'LOGOUT';
} | {
  type: 'ADD_FUNDS';
  payload: number;
} | {
  type: 'DEDUCT_FUNDS';
  payload: number;
} | {
  type: 'ADD_TRANSACTION';
  payload: Transaction;
} | {
  type: 'CREATE_CAMPAIGN';
  payload: Campaign;
} | {
  type: 'UPDATE_CAMPAIGN';
  payload: Campaign;
} | {
  type: 'UPDATE_CAMPAIGN_RAISED';
  payload: {
    id: string;
    amount: number;
  };
} | {
  type: 'ADD_TRANSFER';
  payload: Transfer;
} | {
  type: 'ADD_MAINTENANCE_REQUEST';
  payload: MaintenanceRequest;
} | {
  type: 'UPDATE_MAINTENANCE_STATUS';
  payload: {
    id: string;
    status: string;
  };
} | {
  type: 'ADD_DONATION';
  payload: Donation;
} | {
  type: 'ADD_PAYOUT';
  payload: Payout;
} | {
  type: 'UPDATE_KYC';
  payload: KycData;
} | {
  type: 'UPDATE_USER';
  payload: Partial<User>;
} | {
  type: 'LOAD_STATE';
  payload: AppState;
};
// --- Initial State ---
const initialState: AppState = {
  isAuthenticated: false,
  user: null,
  campaigns: [],
  transactions: [],
  transfers: [],
  properties: [],
  maintenanceRequests: [],
  donations: [],
  payouts: []
};
// --- Reducer ---
function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    case 'ADD_FUNDS':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          walletBalance: state.user.walletBalance + action.payload
        }
      };
    case 'DEDUCT_FUNDS':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          walletBalance: state.user.walletBalance - action.payload
        }
      };
    case 'ADD_TRANSACTION':
      return {
        ...state,
        transactions: [action.payload, ...state.transactions]
      };
    case 'CREATE_CAMPAIGN':
      return {
        ...state,
        campaigns: [action.payload, ...state.campaigns]
      };
    case 'UPDATE_CAMPAIGN':
      return {
        ...state,
        campaigns: state.campaigns.map(c => c.id === action.payload.id ? action.payload : c)
      };
    case 'UPDATE_CAMPAIGN_RAISED':
      return {
        ...state,
        campaigns: state.campaigns.map(c => c.id === action.payload.id ? {
          ...c,
          raisedAmount: c.raisedAmount + action.payload.amount,
          donorCount: (c.donorCount || 0) + 1
        } : c)
      };
    case 'ADD_TRANSFER':
      return {
        ...state,
        transfers: [action.payload, ...state.transfers]
      };
    case 'ADD_MAINTENANCE_REQUEST':
      return {
        ...state,
        maintenanceRequests: [action.payload, ...state.maintenanceRequests]
      };
    case 'UPDATE_MAINTENANCE_STATUS':
      return {
        ...state,
        maintenanceRequests: state.maintenanceRequests.map(r => r.id === action.payload.id ? {
          ...r,
          status: action.payload.status
        } : r)
      };
    case 'ADD_DONATION':
      return {
        ...state,
        donations: [action.payload, ...state.donations]
      };
    case 'ADD_PAYOUT':
      return {
        ...state,
        payouts: [action.payload, ...state.payouts]
      };
    case 'UPDATE_KYC':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          kyc: action.payload
        }
      };
    case 'UPDATE_USER':
      if (!state.user) return state;
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload
        }
      };
    case 'LOAD_STATE':
      return {
        ...action.payload,
        isAuthenticated: state.isAuthenticated
      };
    default:
      return state;
  }
}
// --- Context ---
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<Action>;
  login: () => void;
  logout: () => void;
  processTransaction: (amount: number, type: string, title: string) => string;
} | undefined>(undefined);
export const AppProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  // Load from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('billvast_state');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // Ensure new arrays/fields exist if loading from old state
        if (!parsed.donations) parsed.donations = [];
        if (!parsed.payouts) parsed.payouts = [];
        if (parsed.user && !parsed.user.kyc) {
          parsed.user.kyc = {
            tier: 0,
            status: 'unverified',
            documents: [],
            dailyLimit: 10000
          };
        }
        dispatch({
          type: 'LOAD_STATE',
          payload: parsed
        });
      } catch (e) {
        console.error('Failed to parse saved state', e);
      }
    } else {
      // Seed data if empty
      const seed = generateSeedData();
      const newState: AppState = {
        isAuthenticated: false,
        user: seed.user,
        campaigns: seed.campaigns,
        transactions: seed.transactions,
        transfers: seed.transfers,
        properties: seed.properties,
        maintenanceRequests: seed.maintenanceRequests,
        donations: seed.donations || [],
        payouts: seed.payouts || []
      };
      dispatch({
        type: 'LOAD_STATE',
        payload: newState
      });
      localStorage.setItem('billvast_state', JSON.stringify(newState));
    }
  }, []);
  // Save to localStorage on state change
  useEffect(() => {
    if (state.user) {
      const stateToSave = {
        ...state,
        isAuthenticated: false
      };
      localStorage.setItem('billvast_state', JSON.stringify(stateToSave));
    }
  }, [state]);
  const login = () => {
    if (state.user) {
      dispatch({
        type: 'LOGIN',
        payload: state.user
      });
    } else {
      const seed = generateSeedData();
      dispatch({
        type: 'LOGIN',
        payload: seed.user
      });
    }
  };
  const logout = () => {
    dispatch({
      type: 'LOGOUT'
    });
  };
  const processTransaction = (amount: number, type: string, title: string): string => {
    const reference = generateReference(type.substring(0, 3).toUpperCase());
    if (amount > 0) {
      dispatch({
        type: 'ADD_FUNDS',
        payload: amount
      });
    } else {
      dispatch({
        type: 'DEDUCT_FUNDS',
        payload: Math.abs(amount)
      });
    }
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: `tx_${Date.now()}`,
        type,
        title,
        amount,
        status: 'Completed',
        date: new Date().toISOString(),
        reference
      }
    });
    return reference;
  };
  return <AppContext.Provider value={{
    state,
    dispatch,
    login,
    logout,
    processTransaction
  }} data-id="element-398">
      {children}
    </AppContext.Provider>;
};
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};